import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const variancePillClass = (pct) => {
  if (pct === null || pct === undefined) return 'bg-gray-100 text-gray-600';
  if (Math.abs(pct) <= 10) return 'bg-emerald-100 text-emerald-700';
  if (Math.abs(pct) <= 30) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

const StandardYieldForecast = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [adaptive, setAdaptive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/forecast/standard-yield-curve?days_ahead=30&days_back=30&adaptive=${adaptive}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data);
      } catch (err) {
        console.error('Failed to load standard yield forecast', err);
        setError('Failed to load standard yield forecast');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adaptive]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500" data-testid="standard-yield-loading">Loading standard yield forecast...</div>;
  }
  if (error || !data) {
    return <div className="text-center py-8 text-rose-600" data-testid="standard-yield-error">{error || 'No data available'}</div>;
  }

  const compSummary = data.comparison_summary || {};

  return (
    <div className="space-y-6" data-testid="standard-yield-forecast">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg flex-wrap">
            <span className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Standard Yield Curve — Day-by-Day Forecast
            </span>
            <div className="flex items-center gap-2 text-sm font-normal">
              <button
                type="button"
                data-testid="yield-mode-static"
                onClick={() => setAdaptive(false)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  !adaptive
                    ? 'bg-indigo-600 text-white shadow'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Static curve
              </button>
              <button
                type="button"
                data-testid="yield-mode-adaptive"
                onClick={() => setAdaptive(true)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  adaptive
                    ? 'bg-emerald-600 text-white shadow'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Adaptive (learn from logs)
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-800">
            <p className="font-medium mb-1">
              Active yield curve ({data.curves.mode === 'adaptive' ? 'adaptive — learned from production logs' : 'static — owner-calibrated'}):
            </p>
            <p>Flush 1: {data.curves.flush_1_kg_by_day.map(v => `${Math.round(v)}kg`).join(' → ')}</p>
            <p>Flush 2 (after ~{data.curves.default_gap_days}-day gap): {data.curves.flush_2_kg_by_day.map(v => `${Math.round(v)}kg`).join(' → ')}</p>
            {data.curves.mode === 'adaptive' && (
              <p className="mt-1 text-indigo-700">
                Sample sizes — F1: [{(data.curves.learned_samples_flush_1 || []).join(', ')}],
                F2: [{(data.curves.learned_samples_flush_2 || []).join(', ')}]
                · Days with no history fall back to the static curve.
              </p>
            )}
          </div>

          <Tabs defaultValue="forecast" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="forecast" data-testid="standard-yield-tab-forecast">
                <TrendingUp className="w-4 h-4 mr-1" /> Next 30 Days
              </TabsTrigger>
              <TabsTrigger value="comparison" data-testid="standard-yield-tab-comparison">
                <LineChart className="w-4 h-4 mr-1" /> Forecast vs Actual (Last 30d)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="forecast">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs text-indigo-600 font-medium">Total Forecast</p>
                  <p className="text-2xl font-bold text-indigo-800">{(data.forecast_total_kg / 1000).toFixed(2)}t</p>
                  <p className="text-xs text-indigo-600">{Math.round(data.forecast_total_kg)} kg</p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-600 font-medium">Flush 1</p>
                  <p className="text-2xl font-bold text-green-800">{(data.forecast_flush_totals.flush_1_kg / 1000).toFixed(2)}t</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-600 font-medium">Flush 2</p>
                  <p className="text-2xl font-bold text-amber-800">{(data.forecast_flush_totals.flush_2_kg / 1000).toFixed(2)}t</p>
                </div>
              </div>

              {data.forecast.filter(d => d.forecast_kg > 0).length === 0 ? (
                <p className="text-gray-500 text-center py-6">No production expected in this window.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="standard-yield-forecast-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-600">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2 text-right">Forecast (kg)</th>
                        <th className="px-3 py-2">Breakdown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.forecast.filter(d => d.forecast_kg > 0).map(d => (
                        <tr key={d.date} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                            {format(parseISO(d.date), 'EEE, MMM d')}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-indigo-700">
                            {Math.round(d.forecast_kg).toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {d.contributions.map((c, i) => (
                                <Badge
                                  key={`${d.date}-${i}`}
                                  className={`text-[10px] ${
                                    c.flush === 'Flush 1'
                                      ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                      : 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                                  }`}
                                >
                                  {c.room_name} · {c.flush} D{c.day_in_flush} · {c.kg}kg
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="comparison">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs text-indigo-600 font-medium">Forecast Total</p>
                  <p className="text-2xl font-bold text-indigo-800">{((compSummary.forecast_total_kg || 0) / 1000).toFixed(2)}t</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Actual Total</p>
                  <p className="text-2xl font-bold text-blue-800">{((compSummary.actual_total_kg || 0) / 1000).toFixed(2)}t</p>
                </div>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-xs text-gray-600 font-medium">Variance</p>
                  <p className={`text-2xl font-bold ${(compSummary.variance_kg || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {(compSummary.variance_kg || 0) >= 0 ? '+' : ''}{((compSummary.variance_kg || 0) / 1000).toFixed(2)}t
                  </p>
                </div>
                <div className={`p-3 rounded-lg border ${variancePillClass(compSummary.variance_pct)}`}>
                  <p className="text-xs font-medium">Variance %</p>
                  <p className="text-2xl font-bold flex items-center gap-1">
                    {(compSummary.variance_pct ?? 0) >= 0
                      ? <TrendingUp className="w-5 h-5" />
                      : <TrendingDown className="w-5 h-5" />}
                    {compSummary.variance_pct !== null && compSummary.variance_pct !== undefined
                      ? `${compSummary.variance_pct > 0 ? '+' : ''}${compSummary.variance_pct.toFixed(1)}%`
                      : 'n/a'}
                  </p>
                </div>
              </div>

              {data.comparison.length === 0 ? (
                <p className="text-gray-500 text-center py-6">No data in the comparison window.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="standard-yield-comparison-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-600">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2 text-right">Forecast (kg)</th>
                        <th className="px-3 py-2 text-right">Actual (kg)</th>
                        <th className="px-3 py-2 text-right">Variance</th>
                        <th className="px-3 py-2 text-right">Var %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.comparison.map(d => (
                        <tr key={d.date} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                            {format(parseISO(d.date), 'EEE, MMM d')}
                          </td>
                          <td className="px-3 py-2 text-right text-indigo-700">
                            {Math.round(d.forecast_kg).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right text-blue-700 font-medium">
                            {Math.round(d.actual_kg).toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 text-right font-medium ${
                            d.variance_kg >= 0 ? 'text-emerald-700' : 'text-rose-700'
                          }`}>
                            {d.variance_kg >= 0 ? '+' : ''}{Math.round(d.variance_kg).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${variancePillClass(d.variance_pct)}`}>
                              {d.variance_pct !== null && d.variance_pct !== undefined
                                ? `${d.variance_pct > 0 ? '+' : ''}${d.variance_pct.toFixed(1)}%`
                                : 'n/a'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default StandardYieldForecast;
